<template>
    <div class="payment-link-page d-flex align-items-center justify-content-center py-5">
        <div class="card shadow-sm" style="max-width: 420px; width: 100%;">
            <div class="card-body text-center p-4">

                <div v-if="loading" class="py-5">
                    <div class="spinner-border text-primary" role="status"><span class="sr-only">Loading...</span></div>
                </div>

                <div v-else-if="notFound" class="py-4">
                    <h4>Link not found</h4>
                    <p class="text-muted">This payment link doesn't exist or has been removed.</p>
                </div>

                <template v-else>
                    <img v-if="link.company.logo_url" :src="link.company.logo_url" alt="" class="mb-3" style="max-height: 64px; max-width: 100%;" />
                    <h5 class="mb-1">{{ link.company.name }}</h5>
                    <p class="text-muted mb-3">Payment Request</p>

                    <template v-if="terminalState === 'expired'">
                        <h4 class="text-danger">This payment link is no longer available.</h4>
                        <p class="text-muted">Please contact {{ link.company.name }} for a new payment request.</p>
                    </template>

                    <template v-else-if="terminalState === 'cancelled'">
                        <h4 class="text-danger">This payment link was cancelled.</h4>
                        <p class="text-muted">Please contact {{ link.company.name }} for a new payment request.</p>
                    </template>

                    <template v-else-if="terminalState === 'paid'">
                        <div class="success-checkmark mb-2"><div class="check-icon">
                            <span class="icon-line line-tip"></span>
                            <span class="icon-line line-long"></span>
                            <div class="icon-circle"></div><div class="icon-fix"></div>
                        </div></div>
                        <h4 class="text-primary">Payment successful</h4>
                        <p class="text-muted">Your payment of KSh {{ formatMoney(link.amount) }} to {{ link.company.name }} was received.</p>
                    </template>

                    <template v-else-if="stkState === 'pending'">
                        <div class="spinner-border text-primary my-3" role="status"><span class="sr-only">Loading...</span></div>
                        <p>Please check your phone and enter your M-Pesa PIN to confirm the payment...</p>
                    </template>

                    <template v-else-if="stkState === 'failed'">
                        <h5 class="text-danger"><i class="bi bi-x-circle"></i></h5>
                        <p>{{ stkFailureReason }}</p>
                        <button type="button" class="btn btn-primary btn-sm" @click="stkState = null">Try Again</button>
                    </template>

                    <template v-else>
                        <h2 class="mb-1">KSh {{ formatMoney(link.amount) }}</h2>
                        <p class="text-muted mb-3">{{ link.reason }}</p>

                        <div v-if="!link.dukakit_payments_enabled" class="alert alert-warning">
                            This business is currently unable to accept payments online.
                        </div>

                        <template v-else>
                            <div class="form-group text-left">
                                <label>M-Pesa phone number</label>
                                <vue-tel-input v-on:validate="validatePhone" v-model="phoneNumber"></vue-tel-input>
                                <small v-if="phoneValidMessage" class="text-danger d-block mt-1">{{ phoneValidMessage }}</small>
                            </div>
                            <button
                                type="button"
                                class="btn btn-primary btn-block"
                                :disabled="!!phoneValidMessage || !phoneNumber || paying"
                                @click="payNow"
                            >
                                <span v-if="paying"><i class="bi bi-arrow-repeat spin"></i> Sending...</span>
                                <span v-else>Pay with M-Pesa</span>
                            </button>
                            <small v-if="payError" class="text-danger d-block mt-2">{{ payError }}</small>
                        </template>
                    </template>

                    <p class="text-muted small mt-4 mb-0">Powered by {{ $platformBrand.name }}</p>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'PaymentLinkView',
    props: ['token'],

    data() {
        return {
            loading: true,
            notFound: false,
            link: null,
            phoneNumber: '',
            phoneValidMessage: '',
            paying: false,
            payError: '',
            paymentId: null,
            stkState: null, // null|pending|failed
            stkFailureReason: '',
            pollInterval: null,
        };
    },

    computed: {
        terminalState() {
            if (!this.link) return null;
            if (['expired', 'cancelled', 'paid'].includes(this.link.status)) return this.link.status;
            return null;
        },
    },

    mounted() {
        this.load();
    },

    beforeDestroy() {
        this.stopPolling();
    },

    methods: {
        async load() {
            this.loading = true;
            try {
                const { data } = await axios.get(`/api/public/payment-links/${this.token}`);
                this.link = data;
                this.phoneNumber = data.phone_number || '';
            } catch (err) {
                this.notFound = true;
            } finally {
                this.loading = false;
            }
        },

        validatePhone(phoneNumber) {
            this.phoneValidMessage = phoneNumber.valid ? '' : 'Enter a valid phone number';
        },

        async payNow() {
            this.paying = true;
            this.payError = '';
            try {
                const { data } = await axios.post(`/api/public/payment-links/${this.token}/stk`, {
                    phone_number: this.phoneNumber,
                });
                this.paymentId = data.payment_id;
                this.stkState = 'pending';
                this.startPolling();
            } catch (err) {
                this.payError = err.response?.data?.message || 'Could not initiate payment. Please try again.';
            } finally {
                this.paying = false;
            }
        },

        startPolling() {
            this.stopPolling();
            this.pollInterval = setInterval(async () => {
                try {
                    const { data } = await axios.get(`/api/public/payment-links/${this.token}/status/${this.paymentId}`);

                    if (data.status === 'completed') {
                        this.stopPolling();
                        this.link.status = 'paid';
                        this.stkState = null;
                    }

                    if (data.status === 'failed') {
                        this.stopPolling();
                        this.stkState = 'failed';
                        this.stkFailureReason = data.result_desc || 'There was an issue with the payment.';
                    }
                } catch (err) {
                    // keep polling - a transient network error shouldn't stop the flow
                }
            }, 3000);
        },

        stopPolling() {
            if (this.pollInterval) {
                clearInterval(this.pollInterval);
                this.pollInterval = null;
            }
        },

        formatMoney(v) {
            return Number(v || 0).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
    },
};
</script>

<style scoped>
.payment-link-page { min-height: 90vh; }
.spin { display: inline-block; animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
</style>
